import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaChartBar } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { useI18n } from "../../i18n/I18nContext";
import DateRangePicker from "../common/DateRangePicker";
import MultiSelect from "../common/MultiSelect";
import Switch from "../common/Switch";
import Dropdown, { DropdownItem } from "../common/Dropdown";
import Spinner from "../spinner/Spinner";
import NeedOrganizationEmptyState from "../common/NeedOrganizationEmptyState";
import memberLabel from "../common/memberLabel";
import toastOptions from "../../Redux/toastOptions";
import { listClientsApi } from "../../Redux/Clients/Client.actions";
import { listProjectsApi } from "../../Redux/Projects/Project.actions";
import { listMembersApi } from "../../Redux/Organizations/Org.actions";
import { fetchReportApi, exportReportApi, clearReport } from "../../Redux/Reports/Report.actions";
import { fetchLatestEntryDayApi } from "../../Redux/Tasks/Task.actions";
import { dateRangeShortcuts, toISODate, fromISODate } from "../common/dateRangeShortcuts";
import { isAdminOrOwner as computeIsAdminOrOwner, isSuperadmin, memberRole } from "../common/permissions";
import SummaryReportView from "./SummaryReportView";
import DetailedReportView from "./DetailedReportView";
import styles from "./Styles/Reports.module.css";

// referenceDate defaults to today - pass the user's most recently logged
// time entry's date instead once it's known (see the effect below), so the
// initial range reflects their last activity rather than an empty range
// when they haven't logged anything recently (ai-instruct-63). Start is
// always the first day of that reference date's month.
const defaultRange = (t, referenceDate) => {
  const thisMonth = dateRangeShortcuts(t, referenceDate).find((s) => s.key === "thisMonth");
  const [s, e] = thisMonth.range();
  return { start: toISODate(s), end: toISODate(e) };
};

const Reports = () => {
  const { t } = useI18n();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  // allMembers rather than members: reports cover past work, so the user
  // filter has to be able to offer people who have since been disabled.
  const { currentOrgId, members, allMembers } = useSelector((state) => state.organizations);
  const { clients } = useSelector((state) => state.clients);
  const { projects } = useSelector((state) => state.projects);
  const { data: report, isLoading } = useSelector((state) => state.reports);

  const [tab, setTab] = useState("summary");
  const [range, setRangeState] = useState(() => defaultRange(t));
  const [clientIds, setClientIds] = useState([]);
  const [projectIds, setProjectIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [includeFormerMembers, setIncludeFormerMembers] = useState(false);

  const myRole = memberRole(user, members);
  const canAccess = isSuperadmin(user) || (myRole && myRole !== "reader");
  const isAdminOrOwner = computeIsAdminOrOwner(user, members);

  // Tracks whether the user has touched the date range themselves, so the
  // most-recent-entry lookup below never clobbers a choice they already
  // made - a ref rather than state since it's read from inside an async
  // callback (see the effect below), where a state closure would go stale.
  const rangeTouchedRef = useRef(false);
  const setRange = (start, end) => {
    rangeTouchedRef.current = true;
    setRangeState({ start, end });
  };

  useEffect(() => {
    if (currentOrgId) {
      dispatch(listClientsApi(currentOrgId, user.token));
      dispatch(listProjectsApi(currentOrgId, user.token));
      dispatch(listMembersApi(currentOrgId, user.token, { includeDisabled: true }));
    }
  }, [dispatch, currentOrgId, user.token]);

  // Defaults the date range to the week of the user's most recently logged
  // time entry instead of the real current date, so it isn't just an empty
  // "this week" for someone who hasn't logged anything in a while
  // (ai-instruct-63). Only applies once, and only if the range is still
  // untouched by the time it resolves.
  useEffect(() => {
    if (!currentOrgId) return;
    let cancelled = false;
    dispatch(fetchLatestEntryDayApi(currentOrgId, user.token)).then((day) => {
      if (cancelled || !day || rangeTouchedRef.current) return;
      setRangeState(defaultRange(t, fromISODate(day)));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId]);

  // Narrowing the client filter narrows which projects make sense too, so
  // drop any already-selected project that no longer belongs to one of the
  // selected clients.
  useEffect(() => {
    if (clientIds.length === 0) return;
    setProjectIds((prev) =>
      prev.filter((id) => clientIds.includes(projects.find((p) => p.id === id)?.clientId))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIds, projects]);

  const filters = { type: tab, start: range.start, end: range.end, clientIds, projectIds, userIds };
  const isSummaryReport = report && Array.isArray(report.rows);
  const isDetailedReport = report && Array.isArray(report.entries);
  const isValidRange = range.start <= range.end;

  const refresh = () => {
    if (!currentOrgId || !canAccess) return;
    if (!isValidRange) {
      toast.error(t("errors.invalidDateRange"), toastOptions);
      dispatch(clearReport());
      return;
    }
    dispatch(fetchReportApi(currentOrgId, filters, user.token));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId, canAccess, tab, range.start, range.end, clientIds, projectIds, userIds]);

  if (!currentOrgId) {
    return <NeedOrganizationEmptyState body={t("organizations.selectOrCreateFirst")} />;
  }

  if (!canAccess) {
    return <p className="cw-error">{t("reports.noAccess")}</p>;
  }

  const clientOptions = clients.map((c) => ({ value: c.id, label: c.name }));
  // Archived projects stay filterable here - reports are about history - but
  // are marked so they're not mistaken for something still being worked on.
  const projectOptions = projects
    .filter((p) => clientIds.length === 0 || clientIds.includes(p.clientId))
    .map((p) => ({ value: p.id, label: p.archived ? `${p.name} ${t("projects.archivedSuffix")}` : p.name }));
  // Former members (disabled accounts) are kept out of the picker unless the
  // switch asks for them, so the everyday case only offers people who still
  // work here. Turning the switch back off never drops an already-selected
  // former member from userIds - that would silently change the report on
  // screen - it only takes them out of the list of things to pick.
  const memberOptions = allMembers
    .filter((m) => includeFormerMembers || !m.disabled)
    .map((m) => ({
      value: m.userId,
      label: m.disabled ? t("reports.formerMember", { name: memberLabel(m) }) : memberLabel(m),
    }));

  // The selection summary above the results. Names come from the same lists
  // the pickers are built from, except for users: a selected former member
  // is missing from memberOptions while the switch is off, and their chip
  // still has to name them (and let them be removed).
  const memberChipLabel = (userId) => {
    const m = allMembers.find((x) => x.userId === userId);
    if (!m) return userId;
    return m.disabled ? t("reports.formerMember", { name: memberLabel(m) }) : memberLabel(m);
  };
  const activeFilters = [
    ...clientIds.map((id) => ({
      key: `client-${id}`,
      group: t("common.client"),
      label: clientOptions.find((o) => o.value === id)?.label || id,
      onRemove: () => setClientIds((prev) => prev.filter((v) => v !== id)),
    })),
    ...projectIds.map((id) => ({
      key: `project-${id}`,
      group: t("projects.title"),
      label: projectOptions.find((o) => o.value === id)?.label || id,
      onRemove: () => setProjectIds((prev) => prev.filter((v) => v !== id)),
    })),
    ...userIds.map((id) => ({
      key: `user-${id}`,
      group: t("nav.users"),
      label: memberChipLabel(id),
      onRemove: () => setUserIds((prev) => prev.filter((v) => v !== id)),
    })),
  ];

  const clearAllFilters = () => {
    setClientIds([]);
    setProjectIds([]);
    setUserIds([]);
  };

  const handleExport = (format) => {
    if (!isValidRange) {
      toast.error(t("errors.invalidDateRange"), toastOptions);
      return;
    }
    dispatch(exportReportApi(currentOrgId, filters, format, user.token));
  };

  return (
    <div className={styles.main}>
      <h1 className="cw-title">
        <FaChartBar className={styles.titleIcon} /> {t("nav.reports")}
      </h1>

      <div className={styles.toolbar}>
        <div className={styles.tabs}>
          <button
            type="button"
            className={`${styles.tab} ${tab === "summary" ? styles.tabActive : ""}`}
            onClick={() => setTab("summary")}
          >
            {t("reports.summary")}
          </button>
          <button
            type="button"
            className={`${styles.tab} ${tab === "detailed" ? styles.tabActive : ""}`}
            onClick={() => setTab("detailed")}
          >
            {t("reports.detailed")}
          </button>
        </div>

        <DateRangePicker start={range.start} end={range.end} onChange={setRange} />

        <Dropdown
          title={t("reports.export")}
          align="end"
          triggerClassName={styles.exportTrigger}
          trigger={<>{t("reports.export")}</>}
        >
          {(close) => (
            <>
              <DropdownItem
                onClick={() => {
                  handleExport("csv");
                  close();
                }}
              >
                {t("reports.exportCsv")}
              </DropdownItem>
              <DropdownItem
                onClick={() => {
                  handleExport("pdf");
                  close();
                }}
              >
                {t("reports.exportPdf")}
              </DropdownItem>
            </>
          )}
        </Dropdown>
      </div>

      <div className={styles.filters}>
        <MultiSelect label={t("common.client")} options={clientOptions} selected={clientIds} onChange={setClientIds} />
        <MultiSelect label={t("projects.title")} options={projectOptions} selected={projectIds} onChange={setProjectIds} />
        <MultiSelect label={t("nav.users")} options={memberOptions} selected={userIds} onChange={setUserIds} />
        <label className={styles.formerMembersToggle}>
          <Switch
            checked={includeFormerMembers}
            onChange={(e) => setIncludeFormerMembers(e.target.checked)}
            aria-label={t("reports.includeFormerMembers")}
          />
          {t("reports.includeFormerMembers")}
        </label>
      </div>

      {/* Spells the current selection out above the results, so what's being
          filtered (and how to undo it) is visible without opening each
          dropdown one by one (issue #7). */}
      {activeFilters.length > 0 && (
        <div className={styles.activeFilters}>
          {activeFilters.map((f) => (
            <span key={f.key} className={styles.chip}>
              <span className={styles.chipGroup}>{f.group}</span>
              <span className={styles.chipLabel}>{f.label}</span>
              <button
                type="button"
                className={styles.chipRemove}
                onClick={f.onRemove}
                title={t("common.removeFilter", { name: f.label })}
                aria-label={t("common.removeFilter", { name: f.label })}
              >
                <MdClose />
              </button>
            </span>
          ))}
          <button type="button" className={styles.clearAllFilters} onClick={clearAllFilters}>
            {t("common.clearAllFilters")}
          </button>
        </div>
      )}

      {isLoading && <Spinner />}
      {/* report can still hold the other tab's shape for one render after
          switching tabs, until the new fetch resolves — check the actual
          shape, not just tab === report's presence, or the wrong view can
          be handed the wrong-shaped data and crash. */}
      {!isLoading && isSummaryReport && tab === "summary" && <SummaryReportView report={report} />}
      {!isLoading && isDetailedReport && tab === "detailed" && (
        <DetailedReportView report={report} orgId={currentOrgId} isAdminOrOwner={isAdminOrOwner} onChanged={refresh} />
      )}
    </div>
  );
};

export default Reports;
